import type { Locale } from '@miroom/shared';
import { useCallback } from 'react';
import { usePrefs } from '../../application/stores/prefsStore';
import { en } from './en';
import { ru, type Dictionary } from './ru';

export const DICTIONARIES: Record<Locale, Dictionary> = { ru, en };

type Plural = { one: string; few?: string; many?: string; other: string };
type Leaf = string | Plural;

type Paths<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends Leaf ? `${P}${K}` : Paths<T[K], `${P}${K}.`>;
}[keyof T & string];

export type TKey = Paths<Dictionary>;
export type TVars = Record<string, string | number>;
export type TFunction = (key: TKey, vars?: TVars) => string;

function lookup(dict: Dictionary, key: string): Leaf | undefined {
  let node: unknown = dict;
  for (const part of key.split('.')) {
    if (!node || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node as Leaf | undefined;
}

export function translate(locale: Locale, key: TKey, vars?: TVars): string {
  let value = lookup(DICTIONARIES[locale], key) ?? lookup(ru, key);
  if (value === undefined) return key;
  if (typeof value === 'object') {
    const n = Number(vars?.n ?? 0);
    const cat = new Intl.PluralRules(locale).select(n) as keyof Plural;
    value = value[cat] ?? value.other;
  }
  return vars
    ? value.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m))
    : value;
}

/** React hook: translation function bound to the current locale. */
export function useT(): TFunction {
  const locale = usePrefs((s) => s.locale);
  return useCallback<TFunction>((key, vars) => translate(locale, key, vars), [locale]);
}

/** Message for an ApiError-like value. */
export function errorMessage(t: TFunction, err: unknown): string {
  const code = (err as { code?: string } | null)?.code;
  const key = `errors.${code}` as TKey;
  return code && lookup(DICTIONARIES.ru, key) !== undefined ? t(key) : t('errors.internal');
}

/** "5 minutes ago" style relative time. */
export function relativeTime(locale: string, ts: number, now = Date.now()): string {
  const diff = (ts - now) / 1000;
  const abs = Math.abs(diff);
  if (abs < 45) return translate(locale as 'ru' | 'en', 'time.justNow');
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['minute', 60],
    ['hour', 3600],
    ['day', 86400],
    ['week', 604800],
    ['month', 2592000],
    ['year', 31536000],
  ];
  let unit = units[0]!;
  for (const u of units) if (abs >= u[1]) unit = u;
  return rtf.format(Math.round(diff / unit[1]), unit[0]);
}
